import { Button, Flex, Form, Modal, Row } from 'antd'
import { ModalProps } from 'antd/lib'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import {
  useInvestigationsServiceCreateInvestigationDemandEndpoint,
  useInvestigationsServiceGetListSpecimensEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ListSortDirection,
  SearchSpecimenSortBy
} from 'src/api/requests'
import {
  message,
  pickReferralDoctor,
  ReferralDoctorFields,
  SaveConfirm
} from 'src/components'
import { useSessionContext } from 'src/contexts'
import { parseCategories } from 'src/features/persons/utils'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { InvestigationCategories } from './InvestigationCategories'

type Props = {
  onCancel: () => void
  onSuccess?: (id: string, investigationId: string) => void
  isTb?: boolean
  /** Investigation categories ticked when the modal opens. */
  preselectCategoryIds?: string[]
} & ModalProps

export const InvestigationDemandModal = ({
  onCancel,
  onSuccess,
  isTb,
  preselectCategoryIds,
  ...props
}: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { personId, id } = useParams()
  const { session } = useSessionContext()

  // const reasonClassifierId = useClassifierId(
  //   ClassifierType.TESTING_CONTINGENT,
  //   ClassifierCategory.SCREENING
  // )

  const { data: specimens } = useInvestigationsServiceGetListSpecimensEndpoint(
    {
      pageNumber: 1,
      pageSize: 1,
      personIds: [String(id)],
      sortBy: SearchSpecimenSortBy.CREATED_AT,
      sortDirection: ListSortDirection.DESCENDING
    },
    [id],
    {
      enabled: !!id
    }
  )

  const investigationReasonId = Form.useWatch('investigationReasonId', form)
  const categoriesForm = Form.useWatch('categories', form)

  const isDisabled = useMemo(() => {
    // if (!investigationReasonId) return true

    const parsedCategories = parseCategories({ categories: categoriesForm })

    const hasSelectedInvestigation = parsedCategories.some(
      (item) => item.institutionId
    )

    return !hasSelectedInvestigation
  }, [investigationReasonId, categoriesForm])

  const { mutate, isPending } =
    useInvestigationsServiceCreateInvestigationDemandEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: (data) => {
        message.success(t('consultations.investigation_created_successfully'))
        data?.id && onSuccess?.(data.specimens?.[0]?.id || '', data.id)
        onCancel()
      }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const categories = parseCategories({ categories: values.categories })

    if (categories.length === 0) {
      return message.error(t('laboratory_classifiers.select_category_error'))
    }

    const payload = {
      institutionId: values.laboratoryInstitutionId,
      personId: String(personId || id),
      categories: categories,
      doctorId: String(session?.userId),
      investigationReasonId: values?.investigationReasonId,
      secondaryInvestigationReasonIds: values?.secondaryInvestigationReasonIds,
      ...pickReferralDoctor(values)
    }

    mutate({
      requestBody: payload
    })
  }

  useEffect(() => {
    if (specimens?.data && specimens?.data.length > 0) {
      const latestSpecimen = specimens?.data[0]
      form.setFieldsValue({
        investigationReasonId: latestSpecimen?.demand?.investigationReason?.id,
        secondaryInvestigationReasonIds:
          latestSpecimen?.demand?.secondaryInvestigationReasons?.map(
            (reason) => reason.id
          ) || []
      })
    }
  }, [specimens])

  return (
    <Modal
      width={800}
      open
      centered
      maskClosable={false}
      onCancel={onCancel}
      title={
        isTb
          ? t('consultations.new_tb_investigation_request')
          : t('consultations.new_investigation_request')
      }
      footer={null}
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto'
        }
      }}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Flex vertical gap={16}>
          <Flex vertical gap={24}>
            <InvestigationCategories
              form={form}
              isTb={isTb}
              autoSelectCurrentLaboratory
              preselectCategoryIds={preselectCategoryIds}
            />

            <Row gutter={[16, 12]}>
              <ReferralDoctorFields form={form} />
            </Row>

            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${t('common.confirm')}?`}
                onConfirm={onSubmit}
                okButtonProps={{ loading: isPending }}
              >
                <Button type="primary" disabled={isDisabled}>
                  {t('common.confirm')}
                </Button>
              </SaveConfirm>
            </Flex>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
